import React, { ReactNode, useContext, useMemo } from "react";
import { Menu } from "@pushpress/shared-components";
import { useCalendar } from "../CalendarProvider/CalendarProvider";
import { AppContext } from "../../../context/app";
import { CalendarItemAttendeeFragment } from "./CalendarDetail-generated.hooks";

interface AttendanceMenuProps {
  row?: CalendarItemAttendeeFragment;
  rows?: { [key: string]: CalendarItemAttendeeFragment };
  hideActions?: AttendanceActions[];
  onClose: VoidFunction;
  onSuccess: VoidFunction;
  onRowsLoading?: (rows: CalendarItemAttendeeFragment[]) => void;
  onClearSelection?: VoidFunction;
}

type AttendanceActions =
  | "cancelCheckIn"
  | "cancelReservation"
  | "checkIn"
  | "removeFromWaitList"
  | "removeLateCancel"
  | "addToClass";

const actionsByStatusMap: { [key: string]: AttendanceActions[] } = {
  cancelled: [],
  checkedin: ["cancelCheckIn", "cancelReservation"],
  "late cancel": ["removeLateCancel"],
  "no show": ["checkIn", "cancelReservation"],
  reserved: ["checkIn", "cancelReservation"],
  waitlisted: ["removeFromWaitList", "addToClass"],
};

const hideOnBulkMenu = ["cancelReservation"];

const filterRowsThatSupportAction = (
  rows: { [key: string]: CalendarItemAttendeeFragment },
  action: AttendanceActions,
) => {
  const items: CalendarItemAttendeeFragment[] = Object.values(rows);
  return items.filter((row) => {
    return actionsByStatusMap[row.status].includes(action);
  });
};

const source = "Core - class view";

const AttendanceMenu = (props: AttendanceMenuProps) => {
  const {
    row,
    rows,
    hideActions,
    onClose,
    onSuccess,
    onClearSelection,
    onRowsLoading,
  } = props;

  const app = useContext(AppContext);

  const clientUuid = app.client.uuid || app.client.client_uuid;

  const {
    cancelCheckIn,
    cancelReservation,
    checkIn,
    removeFromWaitList,
    removeLateCancel,
    bulkCheckIn,
    bulkCancelCheckIn,
    bulkCancelReservation,
  } = useCalendar();

  const handleCancelCheckin = async () => {
    onClose();
    onRowsLoading?.([row]);
    try {
      await cancelCheckIn({
        cancelCheckinInput: { clientUuid, checkinUuid: row.checkinUuid },
      });
      onSuccess();
    } catch {
      onRowsLoading?.([]);
    }
  };

  const handleCancelReservation = async () => {
    onClose();
    onRowsLoading?.([row]);
    try {
      await cancelReservation({
        cancelReservationInput: {
          clientUuid,
          reservationId: row.uuid,
          isLateCancel: undefined,
        },
      });
      onSuccess();
    } catch {
      onRowsLoading?.([]);
    }
  };

  const handleCheckin = async () => {
    onClose();
    onRowsLoading?.([row]);
    try {
      await checkIn({
        checkinInput: {
          reservationId: row.uuid,
          overrideRules: true,
          source,
        },
      });
      onSuccess();
    } catch {
      onRowsLoading?.([]);
    }
  };

  const handleRemoveFromWaitList = async () => {
    onClose();
    onRowsLoading?.([row]);
    try {
      await removeFromWaitList({
        cancelReservationInput: {
          clientUuid,
          reservationId: row.uuid,
          isLateCancel: undefined,
        },
      });
      onSuccess();
    } catch {
      onRowsLoading?.([]);
    }
  };

  const handleLateCancel = async () => {
    onClose();
    onRowsLoading?.([row]);
    try {
      await removeLateCancel({
        removeLateCancelInput: {
          clientUuid,
          reservationId: row.uuid,
          isLateCancel: undefined,
        },
      });
      onSuccess();
    } catch {
      onRowsLoading?.([]);
    }
  };

  const handleBulkCheckin = async () => {
    onClose();
    const items = filterRowsThatSupportAction(rows, "checkIn");
    onRowsLoading?.(items);
    onClearSelection?.();
    const reservationIds = items.map((row) => row.uuid);
    try {
      await bulkCheckIn({ bulkCheckinInput: { reservationIds, source } });
      onSuccess();
    } catch {
      onRowsLoading?.([]);
    }
  };

  const handleBulkCancelCheckin = async () => {
    onClose();
    const items = filterRowsThatSupportAction(rows, "cancelCheckIn");
    onRowsLoading?.(items);
    onClearSelection?.();
    const checkinsUuids = items.map((row) => row.checkinUuid);
    try {
      await bulkCancelCheckIn(checkinsUuids);
      onSuccess();
    } catch {
      onRowsLoading?.([]);
    }
  };

  const handleBulkCancelReservation = async () => {
    onClose();
    const items = filterRowsThatSupportAction(rows, "cancelCheckIn");
    onRowsLoading?.(items);
    onClearSelection?.();
    const reservationsUuids = items.map((row) => row.uuid);
    try {
      await bulkCancelReservation(reservationsUuids);
      onSuccess();
    } catch {
      onRowsLoading?.([]);
    }
  };

  const isBulk = !!rows;

  const menuMap: { [key in AttendanceActions]: ReactNode } = {
    cancelCheckIn: (
      <Menu.Item
        key="cancelCheckIn"
        label="Undo check in"
        action={isBulk ? handleBulkCancelCheckin : handleCancelCheckin}
      />
    ),
    cancelReservation: (
      <Menu.Item
        key="cancelReservation"
        label="Cancel reservation"
        action={isBulk ? handleBulkCancelReservation : handleCancelReservation}
      />
    ),
    checkIn: (
      <Menu.Item
        key="checkIn"
        label="Check in"
        action={isBulk ? handleBulkCheckin : handleCheckin}
      />
    ),
    addToClass: (
      <Menu.Item
        key="addToClass"
        label="Add to class"
        action={isBulk ? handleBulkCheckin : handleCheckin}
      />
    ),
    removeFromWaitList: (
      <Menu.Item
        key="removeFromWaitList"
        label="Remove from wait list"
        action={handleRemoveFromWaitList}
      />
    ),
    removeLateCancel: (
      <Menu.Item
        key="removeLateCancel"
        label="Remove late cancel"
        action={handleLateCancel}
      />
    ),
  };

  const menuItems = useMemo(() => {
    if (row) {
      const actions = actionsByStatusMap[row.status];
      return actions.filter((action) => !hideActions?.includes(action));
    }
    const items: CalendarItemAttendeeFragment[] = Object.values(rows);
    const actionsSet = new Set<AttendanceActions>();
    items.forEach((item) => {
      const actions = actionsByStatusMap[item.status];
      actions.forEach((action) => {
        if (!hideOnBulkMenu.includes(action)) {
          actionsSet.add(action);
        }
      });
    });
    return Array.from(actionsSet);
  }, [row, rows, hideActions]);

  return <Menu>{menuItems.map((name) => menuMap[name])}</Menu>;
};

export default AttendanceMenu;
